"use client"

import { useState, useEffect } from "react"
import { apiService } from "../../services/apiService"
import "./AdminDashboard.css"

const ChurchProjects = () => {
  const [projects, setProjects] = useState([])
  const [showForm, setShowForm] = useState(false)
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    status: "ongoing",
    image: null
  })
  const [imagePreview, setImagePreview] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState("")
  const [successMessage, setSuccessMessage] = useState("")
  const [formErrors, setFormErrors] = useState({})

  useEffect(() => {
    loadProjects()
  }, [])

  // Clear success message after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage("")
      }, 5000)
      return () => clearTimeout(timer)
    }
  }, [successMessage])

  const loadProjects = async () => {
    try {
      setIsLoading(true)
      setError("")
      const response = await apiService.getProjects()
      console.log('Projects response:', response)
      
      // Ensure response is an array
      const projectsArray = Array.isArray(response) ? response : 
                          (response.results && Array.isArray(response.results)) ? response.results : 
                          [];
      
      console.log('Processed projects array:', projectsArray)
      setProjects(projectsArray)
    } catch (error) {
      console.error('Error loading projects:', error)
      setError(error.message || 'Failed to load projects')
      setProjects([]) // Reset to empty array on error
    } finally {
      setIsLoading(false)
    }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData({
      ...formData,
      [name]: value,
    })
    // Clear error when user types
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: "",
      })
    }
  }

  const handleImageChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        setFormErrors({
          ...formErrors,
          image: "Image size should not exceed 5MB"
        })
        return
      }
      // Validate file type
      if (!['image/jpeg', 'image/png', 'image/jpg'].includes(file.type)) {
        setFormErrors({
          ...formErrors,
          image: "Only JPG, JPEG and PNG files are allowed"
        })
        return
      }
      setFormData({
        ...formData,
        image: file
      })
      const imageUrl = URL.createObjectURL(file)
      setImagePreview(imageUrl)
      // Clear error if validation passes
      if (formErrors.image) {
        setFormErrors({
          ...formErrors,
          image: ""
        })
      }
    }
  }

  const validateForm = () => {
    const errors = {}
    if (!formData.title.trim()) {
      errors.title = "Title is required"
    }
    if (!formData.description.trim()) {
      errors.description = "Description is required"
    }
    if (!formData.status) {
      errors.status = "Status is required"
    }
    if (!formData.image) {
      errors.image = "Image is required"
    }
    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validateForm()) {
      return
    }
    try {
      setIsLoading(true)
      setError("")
      await apiService.createProject(formData)
      setSuccessMessage('Project created successfully!')
      setFormData({
        title: "",
        description: "",
        status: "ongoing",
        image: null
      })
      setImagePreview(null)
      setShowForm(false)
      await loadProjects()
    } catch (error) {
      console.error('Error creating project:', error)
      setError(error.message || 'Failed to create project')
    } finally {
      setIsLoading(false)
    }
  }

  const deleteProject = async (id) => {
    if (window.confirm("Are you sure you want to delete this project?")) {
      try {
        setIsLoading(true)
        await apiService.deleteProject(id)
        await loadProjects()
        setSuccessMessage('Project deleted successfully!')
      } catch (error) {
        console.error("Error deleting project:", error)
        setError(error.message || "Failed to delete project")
      } finally {
        setIsLoading(false)
      }
    }
  }

  return (
    <div className="admin-section">
      <div className="action-buttons">
        <button className="btn" onClick={() => setShowForm(!showForm)} disabled={isLoading}>
          {showForm ? "Cancel" : "Add New Project"}
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}
      {successMessage && <div className="success-message">{successMessage}</div>}

      {showForm && (
        <div className="form-container">
          <h2 className="form-title">Add New Project</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="title">Title</label>
              <input
                type="text"
                id="title"
                name="title"
                className={`form-control ${formErrors.title ? "error" : ""}`}
                value={formData.title}
                onChange={handleInputChange}
                required
                disabled={isLoading}
              />
              {formErrors.title && <div className="error-message">{formErrors.title}</div>}
            </div>

            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                name="description"
                className={`form-control ${formErrors.description ? "error" : ""}`}
                value={formData.description}
                onChange={handleInputChange}
                required
                disabled={isLoading}
              />
              {formErrors.description && <div className="error-message">{formErrors.description}</div>}
            </div>

            <div className="form-group">
              <label htmlFor="status">Status</label>
              <select
                id="status"
                name="status"
                className={`form-control ${formErrors.status ? "error" : ""}`}
                value={formData.status}
                onChange={handleInputChange}
                required
                disabled={isLoading}
              >
                <option value="ongoing">Ongoing</option>
                <option value="completed">Completed</option>
                <option value="upcoming">Upcoming</option>
              </select>
              {formErrors.status && <div className="error-message">{formErrors.status}</div>}
            </div>

            <div className="form-group">
              <label htmlFor="image">Image <span className="required">*</span></label>
              <div className={`image-upload-container ${formErrors.image ? "error" : ""}`}>
                <input
                  type="file"
                  id="image"
                  accept="image/jpeg,image/png,image/jpg"
                  onChange={handleImageChange}
                  className="image-input"
                  disabled={isLoading}
                  required
                />
                <div className="image-upload-placeholder">
                  <i className="material-icons">image</i>
                  <p>Click to upload image or drag and drop</p>
                  <span>PNG, JPG or JPEG (max. 5MB)</span>
                </div>
              </div>
              {formErrors.image && <div className="error-message">{formErrors.image}</div>}
              {imagePreview && (
                <div className="image-preview">
                  <img 
                    src={imagePreview} 
                    alt="Preview" 
                    style={{
                      maxWidth: '120px',
                      maxHeight: '80px',
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                  <button 
                    type="button" 
                    className="remove-image-btn"
                    onClick={() => {
                      setImagePreview(null)
                      setFormData({...formData, image: null})
                      setFormErrors({
                        ...formErrors,
                        image: "Image is required"
                      })
                    }}
                    disabled={isLoading}
                  >
                    <i className="material-icons">close</i>
                    Remove Image
                  </button>
                </div>
              )}
            </div>

            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)} disabled={isLoading}>
                Cancel
              </button>
              <button type="submit" className="btn" disabled={isLoading}>
                {isLoading ? "Saving..." : "Add Project"}
              </button>
            </div>
          </form>
        </div>
      )}

      <h2 className="section-title">Church Projects</h2>

      <div className="projects-list">
        {isLoading ? (
          <div className="loading">Loading projects...</div>
        ) : projects.length === 0 ? (
          <p>No projects available</p>
        ) : (
          projects.map((project) => (
            <div className="project-item" key={project.id}>
              <div className="project-content">
                <div className="project-header">
                  <h3>{project.title}</h3>
                  <span className={`status-badge status-${project.status}`}>
                    {project.status.charAt(0).toUpperCase() + project.status.slice(1)}
                  </span>
                </div>
                <div className="project-body">
                  {project.image && (
                    <div className="project-image">
                      <img 
                        src={`https://res.cloudinary.com/dunioadyv/image/upload/${project.image.replace('image/upload/', '')}`} 
                        alt={project.title} 
                        crossOrigin="anonymous"
                        style={{
                          maxWidth: '120px',
                          maxHeight: '80px',
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                        onError={(e) => {
                          console.error('Image failed to load:', project.image)
                          e.target.style.display = 'none'
                        }}
                      />
                    </div>
                  )}
                  <div className="project-details">
                    <p>{project.description}</p>
                  </div>
                </div>
              </div>
              <div className="project-actions">
                <button 
                  className="action-btn delete-btn"
                  onClick={() => deleteProject(project.id)}
                  disabled={isLoading}
                >
                  <i className="material-icons">delete</i>
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  )
}

export default ChurchProjects 